import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    platformName: 'HiringOpen',
    supportEmail: 'support@hiringopen.com',
    maxJobDuration: 30,
    jobApprovalRequired: true,
    emailNotifications: true,
    maintenanceMode: false
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>⚙️ System Settings</h1>
          <p>Configure platform settings and preferences</p>
        </div>
        <button className="btn btn-primary" onClick={handleSaveSettings}>
          💾 Save Changes
        </button>
      </div>

      {/* Settings Tabs */}
      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          className={`tab-btn ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Email
        </button>
        <button
          className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button
          className={`tab-btn ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          Payment
        </button>
        <button
          className={`tab-btn ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          API
        </button>
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="settings-section">
          <div className="section-card">
            <h2>🌐 General Settings</h2>

            <div className="form-group">
              <label>Platform Name</label>
              <input
                type="text"
                value={settings.platformName}
                onChange={(e) => handleSettingChange('platformName', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Support Email</label>
              <input
                type="email"
                value={settings.supportEmail}
                onChange={(e) => handleSettingChange('supportEmail', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Maximum Job Duration (days)</label>
              <input
                type="number"
                value={settings.maxJobDuration}
                onChange={(e) => handleSettingChange('maxJobDuration', parseInt(e.target.value))}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.jobApprovalRequired}
                  onChange={(e) => handleSettingChange('jobApprovalRequired', e.target.checked)}
                />
                <span>Require admin approval for job postings</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                />
                <span>Enable Maintenance Mode</span>
              </label>
              <p className="help-text">When enabled, only admins can access the platform</p>
            </div>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <div className="settings-section">
          <div className="section-card">
            <h2>📧 Email Configuration</h2>

            <div className="form-group">
              <label>SMTP Host</label>
              <input
                type="text"
                placeholder="smtp.example.com"
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>SMTP Port</label>
                <input
                  type="number"
                  placeholder="587"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Encryption</label>
                <select className="form-select">
                  <option value="tls">TLS</option>
                  <option value="ssl">SSL</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>SMTP Username</label>
              <input
                type="text"
                placeholder="your-email@example.com"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>SMTP Password</label>
              <input
                type="password"
                placeholder="••••••••"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                />
                <span>Enable email notifications</span>
              </label>
            </div>

            <button className="btn btn-outline">
              📤 Send Test Email
            </button>
          </div>

          <div className="section-card mt-4">
            <h2>📝 Email Templates</h2>
            <p className="section-description">Customize email templates for various notifications</p>

            <div className="templates-list">
              <div className="template-item">
                <span>Welcome Email</span>
                <button className="btn btn-sm btn-outline">Edit</button>
              </div>
              <div className="template-item">
                <span>Password Reset</span>
                <button className="btn btn-sm btn-outline">Edit</button>
              </div>
              <div className="template-item">
                <span>Job Application Confirmation</span>
                <button className="btn btn-sm btn-outline">Edit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <div className="settings-section">
          <div className="section-card">
            <h2>🔒 Security Settings</h2>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" defaultChecked />
                <span>Require email verification for new accounts</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Enable two-factor authentication (2FA)</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" defaultChecked />
                <span>Enable audit logging</span>
              </label>
            </div>

            <div className="form-group">
              <label>Session Timeout (minutes)</label>
              <input
                type="number"
                defaultValue="60"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Maximum Login Attempts</label>
              <input
                type="number"
                defaultValue="5"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Password Minimum Length</label>
              <input
                type="number"
                defaultValue="8"
                className="form-input"
              />
            </div>
          </div>

          <div className="section-card mt-4">
            <h2>🔑 API Keys</h2>
            <p className="section-description">Manage API keys for third-party integrations</p>

            <div className="api-keys-list">
              <div className="api-key-item">
                <div>
                  <strong>Production API Key</strong>
                  <p className="text-muted">Created on Jan 1, 2024</p>
                </div>
                <button className="btn btn-sm btn-danger">Revoke</button>
              </div>
            </div>

            <button className="btn btn-outline mt-3">
              ➕ Generate New API Key
            </button>
          </div>
        </div>
      )}

      {/* Payment Settings */}
      {activeTab === 'payment' && (
        <div className="settings-section">
          <div className="section-card">
            <h2>💳 Payment Gateway Settings</h2>

            <div className="form-group">
              <label>Payment Provider</label>
              <select className="form-select">
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="razorpay">Razorpay</option>
              </select>
            </div>

            <div className="form-group">
              <label>Publishable Key</label>
              <input
                type="text"
                placeholder="pk_live_..."
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Secret Key</label>
              <input
                type="password"
                placeholder="sk_live_..."
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Currency</label>
              <select className="form-select">
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="INR">INR - Indian Rupee</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Enable test mode</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* API Settings */}
      {activeTab === 'api' && (
        <div className="settings-section">
          <div className="section-card">
            <h2>🔌 API Configuration</h2>

            <div className="form-group">
              <label>API Base URL</label>
              <input
                type="text"
                value="https://api.hiringopen.com/v1"
                className="form-input"
                readOnly
              />
            </div>

            <div className="form-group">
              <label>Rate Limiting</label>
              <select className="form-select">
                <option value="100">100 requests/minute</option>
                <option value="500">500 requests/minute</option>
                <option value="1000">1000 requests/minute</option>
                <option value="unlimited">Unlimited</option>
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" defaultChecked />
                <span>Enable API documentation</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input type="checkbox" defaultChecked />
                <span>Enable CORS</span>
              </label>
            </div>

            <div className="form-group">
              <label>Allowed Origins (comma-separated)</label>
              <textarea
                className="form-textarea"
                rows="3"
                placeholder="https://example.com, https://app.example.com"
              />
            </div>
          </div>

          <div className="section-card mt-4">
            <h2>📚 API Documentation</h2>
            <p className="section-description">
              Access comprehensive API documentation for developers
            </p>
            <button className="btn btn-outline">
              📖 View API Docs
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
